import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItemText from '@mui/material/ListItemText';
import ListItemButton from '@mui/material/ListItemButton';
import Divider from '@mui/material/Divider';
import Collapse from '@mui/material/Collapse';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import { useAuth } from '../auth/context/AuthContext';
import Avatar from '@mui/material/Avatar';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import Typography from '@mui/material/Typography';
import LogoutIcon from '@mui/icons-material/Logout';
import CodeIcon from '@mui/icons-material/Code';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckIcon from '@mui/icons-material/Check';
import { toast } from 'react-toastify';
import { updateProgrammingLanguage } from '../auth/api/authApi';
import { getLanguageDisplayName, PROGRAMMING_LANGUAGES } from '../constants/trainerConstants';
import { extractErrorMessage } from '../api/apiClient';
import { useThemeMode } from '../theme/ThemeModeProvider';
import '../css/Navbar.css';

const Navbar = () => {
	const [drawerOpen, setDrawerOpen] = useState(false);
	const [langExpanded, setLangExpanded] = useState(false);
	const [scrolled, setScrolled] = useState(false);
	const location = useLocation();
	const { user, isAuthenticated, isInitializing, logout, updateUser } = useAuth();
	const { mode, toggleThemeMode } = useThemeMode();
	const isDark = mode === 'dark';
	const [anchorEl, setAnchorEl] = useState(null);
	const menuOpen = Boolean(anchorEl);
	const handleOpenMenu = (e) => setAnchorEl(e.currentTarget);
	const handleCloseMenu = () => setAnchorEl(null);
	const handleLogout = () => { handleCloseMenu(); logout(); };

	const [languageAnchorEl, setLanguageAnchorEl] = useState(null);
	const languageMenuOpen = Boolean(languageAnchorEl);
	const handleOpenLanguageMenu = (e) => setLanguageAnchorEl(e.currentTarget);
	const handleCloseLanguageMenu = () => setLanguageAnchorEl(null);
	const themeToggleLabel = isDark ? 'Включить светлую тему' : 'Включить темную тему';
	const ThemeIcon = isDark ? LightModeOutlinedIcon : DarkModeOutlinedIcon;

	const handleLanguageChange = async (language) => {
		handleCloseLanguageMenu();
		handleCloseMenu();
		try {
			await updateProgrammingLanguage(language);
			updateUser({ ...user, programmingLanguage: language });
			toast.success('Язык обновлен');
		} catch (error) {
			toast.error(extractErrorMessage(error));
		}
	};

	const handleThemeToggle = (event) => {
		event.stopPropagation();
		toggleThemeMode();
	};

	useEffect(() => {
		const handleScroll = () => {
			setScrolled(window.scrollY > 20);
		};
		window.addEventListener('scroll', handleScroll);
		return () => window.removeEventListener('scroll', handleScroll);
	}, []);

	const toggleDrawer = (open) => (event) => {
		if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
			return;
		}
		setDrawerOpen(open);
		if (!open) setLangExpanded(false);
	};

	const storageBaseUrl = process.env.REACT_APP_STORAGE_BASE_URL;
	const logoUrl = `${storageBaseUrl}/images/shared/logo.navbar.svg`;

	const isActive = (path) => location.pathname === path;

	const navLinks = [
		{ to: '/patterns', label: 'Паттерны' },
	];

	return (
		<AppBar
			position="fixed"
			elevation={0}
			sx={{
				bgcolor: scrolled ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.8)',
				backdropFilter: 'blur(12px)',
				borderBottom: scrolled ? '1px solid rgba(var(--gof-rgb-primary), 0.08)' : '1px solid transparent',
				transition: 'all 0.3s ease',
				color: 'var(--gof-color-primary)',
				'body[data-theme="dark"] &': {
					background: scrolled
						? 'linear-gradient(135deg, rgba(49, 125, 179, 0.98) 0%, rgba(21, 53, 77, 0.97) 42%, rgba(1, 39, 66, 0.99) 100%)'
						: 'linear-gradient(135deg, rgba(49, 125, 179, 0.9) 0%, rgba(21, 53, 77, 0.9) 42%, rgba(1, 39, 66, 0.94) 100%)',
					borderBottom: scrolled ? '1px solid rgba(83, 139, 179, 0.28)' : '1px solid transparent',
					color: '#F2F7FB',
				},
			}}
		>
			<Toolbar
				sx={{
					maxWidth: 'lg',
					width: '100%',
					mx: 'auto',
					px: { xs: 2, sm: 3 },
					minHeight: { xs: 64, md: 72 },
				}}
			>
				<IconButton
					edge="start"
					aria-label="menu"
					sx={{
						mr: 1,
						display: { xs: 'flex', md: 'none' },
						color: 'var(--gof-color-primary)',
						'body[data-theme="dark"] &': {
							color: '#033c66',
						},
					}}
					onClick={toggleDrawer(true)}
				>
					<MenuIcon />
				</IconButton>

				<Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
					<Box
						component={Link}
						to="/"
						sx={{
							position: 'relative',
							display: 'flex',
							alignItems: 'center',
							textDecoration: 'none',
						}}
					>
						<Box
							component="img"
							src={logoUrl}
							alt="GOFINSIGHTS logo"
							sx={{
								height: 36,
								width: 'auto',
								position: 'relative',
								zIndex: 1,
							}}
						/>
					</Box>
				</Box>

				<Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1 }}>
					{navLinks.map((link) => (
						<Button
							key={link.to}
							component={Link}
							to={link.to}
							sx={{
								color: isActive(link.to) ? 'var(--gof-color-accent)' : 'var(--gof-color-text-secondary)',
								fontWeight: isActive(link.to) ? 600 : 500,
								fontSize: '0.95rem',
								textTransform: 'none',
								px: 2,
								py: 1,
								borderRadius: '8px',
								position: 'relative',
								'&:hover': {
									bgcolor: 'rgba(var(--gof-rgb-accent), 0.06)',
									color: 'var(--gof-color-accent)',
								},
								'body[data-theme="dark"] &': {
									color: isActive(link.to) ? '#fc6403' : 'rgba(242, 247, 251, 0.86)',
									'&:hover': {
										bgcolor: 'rgba(252, 100, 3, 0.14)',
										color: '#fff',
									},
								},
								'&::after': isActive(link.to) ? {
									content: '""',
									position: 'absolute',
									bottom: 4,
									left: '50%',
									transform: 'translateX(-50%)',
									width: 20,
									height: 2,
									bgcolor: 'var(--gof-color-accent)',
									borderRadius: 1,
								} : {},
							}}
						>
							{link.label}
						</Button>
					))}
					<Tooltip title={themeToggleLabel}>
						<IconButton
							aria-label={themeToggleLabel}
							onClick={handleThemeToggle}
							sx={{
								ml: 0.5,
								color: 'var(--gof-color-primary)',
								border: '1px solid var(--gof-color-border)',
								bgcolor: 'rgba(var(--gof-rgb-primary), 0.04)',
								'body[data-theme="dark"] &': {
									color: 'rgba(242, 247, 251, 0.9)',
									borderColor: 'rgba(242, 247, 251, 0.22)',
									bgcolor: 'rgba(242, 247, 251, 0.08)',
								},
								'&:hover': {
									bgcolor: 'rgba(var(--gof-rgb-accent), 0.1)',
									color: 'var(--gof-color-accent)',
								},
							}}
						>
							<ThemeIcon fontSize="small" />
						</IconButton>
					</Tooltip>
					<Button
						component={Link}
						to="/trainer"
						variant="contained"
						startIcon={<FitnessCenterIcon sx={{ fontSize: '18px !important' }} />}
						sx={{
							ml: 1,
							bgcolor: 'var(--gof-color-accent)',
							color: '#fff',
							fontWeight: 600,
							fontSize: '0.9rem',
							textTransform: 'none',
							px: 2.5,
							py: 1,
							borderRadius: '10px',
							boxShadow: '0 2px 8px rgba(var(--gof-rgb-accent), 0.3)',
							'&:hover': {
								bgcolor: 'var(--gof-color-accent-hover)',
								boxShadow: '0 4px 16px rgba(var(--gof-rgb-accent), 0.4)',
							},
							'body[data-theme="dark"] &': {
								bgcolor: '#fc6403',
								'&:hover': {
									bgcolor: '#CF5E34',
								},
							},
						}}
					>
						Тренажер
					</Button>
					{!isInitializing && !isAuthenticated && (
						<Button
							component={Link}
							to="/auth/login"
							sx={{
								ml: 1,
								color: 'var(--gof-color-primary)',
								fontWeight: 600,
								fontSize: '0.9rem',
								textTransform: 'none',
								px: 2.5,
								py: 1,
								borderRadius: '10px',
								border: '1px solid rgba(var(--gof-rgb-primary), 0.2)',
								'body[data-theme="dark"] &': {
									color: '#fff',
									borderColor: 'rgba(255, 255, 255, 0.28)',
								},
								'&:hover': {
									bgcolor: 'rgba(var(--gof-rgb-primary), 0.06)',
									borderColor: 'rgba(var(--gof-rgb-primary), 0.3)',
								},
							}}
						>
							Войти
						</Button>
					)}
					{isAuthenticated && user && (
						<>
							<IconButton onClick={handleOpenMenu} sx={{ ml: 1 }}>
								<Avatar sx={{ width: 36, height: 36, bgcolor: 'var(--gof-color-primary)', fontSize: '0.9rem', fontWeight: 600 }}>
									{user.email[0].toUpperCase()}
								</Avatar>
							</IconButton>
							<Menu
								anchorEl={anchorEl}
								open={menuOpen}
								onClose={handleCloseMenu}
								disableScrollLock
								PaperProps={{
									sx: {
										mt: 1,
										borderRadius: '12px',
										border: '1px solid var(--gof-color-border)',
										boxShadow: '0 4px 20px rgba(var(--gof-rgb-primary), 0.1)',
										minWidth: 180,
									},
								}}
							>
								<MenuItem onClick={handleOpenLanguageMenu} sx={{ fontSize: '0.9rem', color: 'var(--gof-color-text-secondary)', py: 1.5 }}>
									<ListItemIcon><CodeIcon fontSize="small" /></ListItemIcon>
									<Box sx={{ display: 'flex', flexDirection: 'column' }}>
										<Typography sx={{ fontSize: '0.9rem' }}>Язык</Typography>
										<Typography sx={{ fontSize: '0.78rem', color: 'var(--gof-color-muted)' }}>
											{user.programmingLanguage ? getLanguageDisplayName(user.programmingLanguage) : 'Не выбран'}
										</Typography>
									</Box>
								</MenuItem>
								<Divider />
								<MenuItem onClick={handleLogout} sx={{ fontSize: '0.9rem', color: 'var(--gof-color-text-secondary)', py: 1.5 }}>
									<ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
									Выйти
								</MenuItem>
							</Menu>
							<Menu
								anchorEl={languageAnchorEl}
								open={languageMenuOpen}
								onClose={handleCloseLanguageMenu}
								disableScrollLock
								PaperProps={{
									sx: {
										borderRadius: '12px',
										border: '1px solid var(--gof-color-border)',
										boxShadow: '0 4px 20px rgba(var(--gof-rgb-primary), 0.1)',
										minWidth: 200,
										p: 1,
									},
								}}
							>
								{PROGRAMMING_LANGUAGES.map((lang) => (
									<MenuItem
										key={lang.value}
										selected={user.programmingLanguage === lang.value}
										onClick={() => handleLanguageChange(lang.value)}
										sx={{ borderRadius: '8px', fontSize: '0.9rem' }}
									>
										{lang.label}
									</MenuItem>
								))}
							</Menu>
						</>
					)}
				</Box>
			</Toolbar>

			<Drawer
				anchor="left"
				open={drawerOpen}
				onClose={toggleDrawer(false)}
				PaperProps={{
					sx: {
						width: 280,
						bgcolor: 'var(--gof-color-surface)',
						pt: 1,
					},
				}}
			>
				<Box
					role="presentation"
					onClick={toggleDrawer(false)}
					onKeyDown={toggleDrawer(false)}
				>
					<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5 }}>
						<Box
							component={Link}
							to="/"
							sx={{
								position: 'relative',
								display: 'flex',
								alignItems: 'center',
								textDecoration: 'none',
							}}
						>
							<Box
								component="img"
								src={logoUrl}
								alt="GOFINSIGHTS logo"
								sx={{
									height: 32,
									width: 'auto',
									position: 'relative',
									zIndex: 1,
								}}
							/>
						</Box>
						<IconButton onClick={toggleDrawer(false)} sx={{ color: 'var(--gof-color-text-secondary)' }}>
							<CloseIcon />
						</IconButton>
					</Box>
					<Divider />
					<List sx={{ px: 1, py: 1 }}>
						<ListItemButton
							onClick={handleThemeToggle}
							sx={{
								borderRadius: '8px',
								mb: 0.5,
								color: 'var(--gof-color-primary)',
								bgcolor: 'rgba(var(--gof-rgb-primary), 0.05)',
								'&:hover': { bgcolor: 'rgba(var(--gof-rgb-accent), 0.1)' },
							}}
						>
							<ThemeIcon sx={{ fontSize: 20, color: 'var(--gof-color-accent)', mr: 1.5 }} />
							<ListItemText
								primary={themeToggleLabel}
								primaryTypographyProps={{ fontWeight: 600 }}
							/>
						</ListItemButton>
						<ListItemButton
							component={Link}
							to="/patterns"
							sx={{
								borderRadius: '8px',
								mb: 0.5,
								bgcolor: isActive('/patterns') ? 'rgba(var(--gof-rgb-primary), 0.08)' : 'transparent',
								color: isActive('/patterns') ? 'var(--gof-color-primary)' : 'var(--gof-color-text-secondary)',
								'&:hover': { bgcolor: 'rgba(var(--gof-rgb-primary), 0.06)' },
							}}
						>
							<ListItemText
								primary="Паттерны"
								primaryTypographyProps={{ fontWeight: isActive('/patterns') ? 600 : 500 }}
							/>
						</ListItemButton>
						<ListItemButton
							component={Link}
							to="/trainer"
							sx={{
								borderRadius: '8px',
								mb: 0.5,
								bgcolor: 'rgba(var(--gof-rgb-accent), 0.08)',
								'&:hover': { bgcolor: 'rgba(var(--gof-rgb-accent), 0.12)' },
							}}
						>
							<FitnessCenterIcon sx={{ fontSize: 20, color: 'var(--gof-color-accent)', mr: 1.5 }} />
							<ListItemText
								primary="Тренажер"
								primaryTypographyProps={{ fontWeight: 600, color: 'var(--gof-color-accent)' }}
							/>
						</ListItemButton>
					</List>
					<Divider sx={{ my: 1 }} />
					{!isAuthenticated ? (
						<List sx={{ px: 1 }}>
							<ListItemButton
								component={Link}
								to="/auth/login"
								sx={{ borderRadius: '8px', mb: 0.5, color: 'var(--gof-color-primary)', '&:hover': { bgcolor: 'rgba(var(--gof-rgb-primary), 0.06)' } }}
							>
								<ListItemText primary="Войти" primaryTypographyProps={{ fontWeight: 600 }} />
							</ListItemButton>
							<ListItemButton
								component={Link}
								to="/auth/register"
								sx={{ borderRadius: '8px', mb: 0.5, bgcolor: 'rgba(var(--gof-rgb-accent), 0.08)', '&:hover': { bgcolor: 'rgba(var(--gof-rgb-accent), 0.12)' } }}
							>
								<ListItemText primary="Регистрация" primaryTypographyProps={{ fontWeight: 600, color: 'var(--gof-color-accent)' }} />
							</ListItemButton>
						</List>
					) : (
						<>
							<Box sx={{ px: 2, py: 1.5 }}>
								<Typography sx={{ fontSize: '0.85rem', color: 'var(--gof-color-muted)', fontWeight: 500 }}>
									{user?.email}
								</Typography>
							</Box>
							<List sx={{ px: 1 }}>
								{/* Language selector */}
								<ListItemButton
									onClick={(e) => {
										e.stopPropagation();
										setLangExpanded((prev) => !prev);
									}}
									sx={{ borderRadius: '8px', color: 'var(--gof-color-text-secondary)', '&:hover': { bgcolor: 'rgba(var(--gof-rgb-primary), 0.06)' } }}
								>
									<CodeIcon sx={{ fontSize: 20, color: 'var(--gof-color-muted)', mr: 1.5 }} />
									<ListItemText
										primary="Язык"
										secondary={user?.programmingLanguage ? getLanguageDisplayName(user.programmingLanguage) : 'Не выбран'}
										primaryTypographyProps={{ fontWeight: 500, fontSize: '0.9rem' }}
										secondaryTypographyProps={{ fontSize: '0.78rem' }}
									/>
									{langExpanded
										? <ExpandLessIcon sx={{ fontSize: 18, color: 'var(--gof-color-muted)' }} />
										: <ExpandMoreIcon sx={{ fontSize: 18, color: 'var(--gof-color-muted)' }} />
									}
								</ListItemButton>
								<Collapse in={langExpanded} timeout="auto">
									<List disablePadding sx={{ pl: 1, pb: 0.5 }}>
										{PROGRAMMING_LANGUAGES.map((lang) => {
											const selected = user?.programmingLanguage === lang.value;
											return (
												<ListItemButton
													key={lang.value}
													onClick={() => handleLanguageChange(lang.value)}
													sx={{
														borderRadius: '8px',
														py: 0.75,
														bgcolor: selected ? 'rgba(var(--gof-rgb-primary), 0.06)' : 'transparent',
														'&:hover': { bgcolor: 'rgba(var(--gof-rgb-primary), 0.06)' },
													}}
												>
													<ListItemText
														primary={lang.label}
														primaryTypographyProps={{
															fontSize: '0.875rem',
															fontWeight: selected ? 600 : 400,
															color: selected ? 'var(--gof-color-primary)' : 'var(--gof-color-text-secondary)',
														}}
													/>
													{selected && <CheckIcon sx={{ fontSize: 16, color: 'var(--gof-color-primary)' }} />}
												</ListItemButton>
											);
										})}
									</List>
								</Collapse>

								<Divider sx={{ my: 0.5 }} />

								<ListItemButton
									onClick={handleLogout}
									sx={{ borderRadius: '8px', color: 'var(--gof-color-text-secondary)', '&:hover': { bgcolor: 'rgba(var(--gof-rgb-primary), 0.06)' } }}
								>
									<LogoutIcon sx={{ fontSize: 20, color: 'var(--gof-color-muted)', mr: 1.5 }} />
									<ListItemText primary="Выйти" primaryTypographyProps={{ fontWeight: 500 }} />
								</ListItemButton>
							</List>
						</>
					)}
					<Divider sx={{ my: 1 }} />
					<List sx={{ px: 1 }}>
						<ListItemButton
							component={Link}
							to="/privacy-policy"
							sx={{ borderRadius: '8px', py: 1 }}
						>
							<ListItemText
								primary="Политика конфиденциальности"
								primaryTypographyProps={{ fontSize: '0.875rem', color: 'var(--gof-color-muted)' }}
							/>
						</ListItemButton>
						<ListItemButton
							component={Link}
							to="/terms-of-service"
							sx={{ borderRadius: '8px', py: 1 }}
						>
							<ListItemText
								primary="Условия обслуживания"
								primaryTypographyProps={{ fontSize: '0.875rem', color: 'var(--gof-color-muted)' }}
							/>
						</ListItemButton>
					</List>
				</Box>
			</Drawer>
		</AppBar>
	);
};

export default Navbar;
